import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { SearchSelect } from '../components/SearchSelect';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Zap, Trash2, User, MapPin, Loader2, MoreVertical, Copy } from 'lucide-react';

export default function Schemas() {
  const navigate = useNavigate();
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState({ name: '', customer_id: '', property_id: '' });
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchSchemas = async () => {
    try {
      const res = await api.get('/schemas');
      setSchemas(res.data || []);
    } catch (e) {
      toast.error('Kon schema\'s niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchemas();
    api.get('/customers?limit=1000').then(r => setCustomers(Array.isArray(r.data) ? r.data : r.data.customers || [])).catch(() => {});
    api.get('/properties').then(r => setProperties(Array.isArray(r.data) ? r.data : r.data.properties || [])).catch(() => {});
  }, []);

  const customerProps = properties.filter(p => !form.customer_id || p.customer_id === form.customer_id);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Geef het schema een naam'); return; }
    setCreating(true);
    try {
      const res = await api.post('/schemas', {
        name: form.name.trim(),
        customer_id: form.customer_id || null,
        property_id: form.property_id || null
      });
      setDialogOpen(false);
      navigate(`/schemas/${res.data.id}`);
    } catch (e) {
      toast.error('Kon schema niet aanmaken');
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (s) => {
    try {
      const res = await api.post(`/schemas/${s.id}/duplicate`);
      setSchemas(prev => [res.data, ...prev]);
      toast.success('Schema gedupliceerd');
    } catch (e) {
      toast.error('Kon schema niet dupliceren');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/schemas/${deleteTarget.id}`);
      setSchemas(prev => prev.filter(s => s.id !== deleteTarget.id));
      toast.success('Schema verwijderd');
    } catch (e) {
      toast.error('Kon schema niet verwijderen');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="schemas-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold">Schema's</h1>
          <p className="text-muted-foreground text-sm mt-1">Eendraadschema's en situatieplannen voor elektrische installaties</p>
        </div>
        <Button onClick={() => { setForm({ name: '', customer_id: '', property_id: '' }); setDialogOpen(true); }} data-testid="new-schema-btn">
          <Plus className="w-4 h-4 mr-2" /> Nieuw schema
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : schemas.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center text-muted-foreground">
            <Zap className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Nog geen schema's. Maak je eerste eendraadschema aan.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {schemas.map(s => (
            <Card key={s.id} className="border-border/50 hover:border-orange-500/40 transition-colors cursor-pointer"
              onClick={() => navigate(`/schemas/${s.id}`)} data-testid={`schema-card-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{s.name}</p>
                    {s.customer_name && <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><User className="w-3.5 h-3.5" />{s.customer_name}</p>}
                    {s.property_address && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{s.property_address}</p>}
                    <p className="text-xs text-muted-foreground/70 mt-2">Bewerkt: {new Date(s.updated_at).toLocaleDateString('nl-BE')}</p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0"
                        onClick={(e) => e.stopPropagation()} data-testid={`schema-menu-${s.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => handleDuplicate(s)} data-testid={`duplicate-schema-${s.id}`}>
                        <Copy className="w-4 h-4 mr-2" /> Dupliceren
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setDeleteTarget(s)} data-testid={`delete-schema-${s.id}`}>
                        <Trash2 className="w-4 h-4 mr-2" /> Verwijderen
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nieuw schema</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Naam (bv. Eendraadschema Bergstraat 32)" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="schema-name-input" />
            <SearchSelect
              options={customers.map(c => ({ value: c.id, label: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.company_name || c.email }))}
              value={form.customer_id}
              onChange={v => setForm(f => {
                const cust = customers.find(c => c.id === v);
                const custName = cust ? (`${cust.first_name || ''} ${cust.last_name || ''}`.trim() || cust.company_name || '') : '';
                return { ...f, customer_id: v, property_id: '', name: custName || f.name };
              })}
              placeholder="Klant (optioneel)"
            />
            <SearchSelect
              options={customerProps.map(p => ({ value: p.id, label: `${p.street || ''} ${p.number || ''}, ${p.city || ''}` }))}
              value={form.property_id}
              onChange={v => setForm(f => ({ ...f, property_id: v }))}
              placeholder="Adres (optioneel)"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuleren</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="create-schema-btn">
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Aanmaken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Schema verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteTarget?.name}" wordt definitief verwijderd.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleDelete} data-testid="confirm-delete-schema">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
